"use client"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useForm } from "react-hook-form"
import { FaLanguage, FaCheck, FaTimes, FaExclamationTriangle } from "react-icons/fa"
import {
  addLanguageAsync,
  deleteLanguageAsync,
  fetchLanguagesAsync,
  selectLanguages,
  updateLanguageAsync,
} from "../CandidateProfileSlice/Language/LanguageSlice"
import {
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useDisclosure,
  Box,
  Skeleton,
} from "@chakra-ui/react"
import EmptyState from "./Tester"

function Language() {
  const dispatch = useDispatch()
  const languages = useSelector(selectLanguages)

  const [selectedLanguage, setSelectedLanguage] = useState(null)
  const [deletingLanguage, setDeletingLanguage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const [duplicateError, setDuplicateError] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  const hasFetchedLanguages = useRef(false)

  const { isOpen: isLanguageModalOpen, onOpen: onLanguageModalOpen, onClose: onLanguageModalClose } = useDisclosure()
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      language: "",
      can_read: false,
      can_write: false,
      can_speak: false,
    },
  })

  const toast = useToast()

  const refreshLanguages = async () => {
    setIsInitialLoading(true)
    try {
      await dispatch(fetchLanguagesAsync())
    } finally {
      setIsInitialLoading(false)
    }
  }

  useEffect(() => {
    if (!hasFetchedLanguages.current) {
      hasFetchedLanguages.current = true
      refreshLanguages()
    } else {
      setIsInitialLoading(false)
    }
  }, [dispatch])

  // Array of common languages
  const languageOptions = [
    'English',
    'Hindi',
    'Bengali',
    'Telugu',
    'Marathi',
    'Tamil',
    'Urdu',
    'Gujarati',
    'Kannada',
    'Malayalam',
    'Punjabi',
    'French',
    'German',
    'Spanish',
    'Chinese',
    'Japanese',
    'Korean',
    'Arabic',
    'Russian',
    'Portuguese',
  ]


  // Check if language already exists
  const checkDuplicateLanguage = (languageName, currentUuid = null) => {
    if (!languageName) return false
    const normalizedInput = languageName.trim().toLowerCase()
    return (
      languages &&
      languages.some(
        (lang) => lang.language.toLowerCase() === normalizedInput && (!currentUuid || currentUuid !== lang.uuid),
      )
    )
  }

  const openAddModal = () => {
    reset({
      language: "",
      can_read: false,
      can_write: false,
      can_speak: false,
    })
    setIsUpdate(false)
    setSelectedLanguage(null)
    setDuplicateError(false)
    onLanguageModalOpen()
  }

  const openUpdateModal = (language) => {
    if (!language) return
    setSelectedLanguage(language)
    setIsUpdate(true)
    setDuplicateError(false)
    // Prefill
    reset({
      language: language.language || "",
      can_read: !!language.can_read,
      can_write: !!language.can_write,
      can_speak: !!language.can_speak,
    })
    onLanguageModalOpen()
  }

  const handleSave = async (newLanguage) => {
    // Check for duplicate languages at submission time
    const isDuplicate = checkDuplicateLanguage(newLanguage.language)
    if (isDuplicate) {
      setDuplicateError(true)
      toast({
        title: "Duplicate Language",
        description: "You have already selected this language",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      return
    }

    setLoading(true)
    try {
      await dispatch(addLanguageAsync({ newLanguage }))
      await refreshLanguages()
      onLanguageModalClose()
      setDuplicateError(false)
      reset()
      toast({
        title: "Language Added",
        description: "Your language has been added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to add language", error)
      toast({
        title: "Error",
        description: "Failed to add language",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async (languageData) => {
    // Check for duplicate languages at submission time
    const isDuplicate = checkDuplicateLanguage(languageData.language, selectedLanguage?.uuid)
    if (isDuplicate) {
      setDuplicateError(true)
      toast({
        title: "Duplicate Language",
        description: "You have already selected this language",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setLoading(true)
    if (selectedLanguage) {
      try {
        await dispatch(
          updateLanguageAsync({
            uuid: selectedLanguage.uuid,
            LanguageData: languageData,
          }),
        )
        await refreshLanguages()
        setSelectedLanguage(null)
        setDuplicateError(false)
        reset()
        onLanguageModalClose()
        toast({
          title: "Language Updated",
          description: "Your language has been updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        })
      } catch (error) {
        console.error("Failed to update language", error)
        toast({
          title: "Error",
          description: "Failed to update language",
          status: "error",
          duration: 3000,
          isClosable: true,
        })
      } finally {
        setLoading(false)
      }
    }
  }

  const handleFormSubmit = (data) => {
    // Require at least one proficiency
    const anyProficiency = data.can_read || data.can_write || data.can_speak
    if (!anyProficiency) {
      toast({
        title: "Select Proficiency",
        description: "Please select at least one proficiency (Read, Write, or Speak).",
        status: "warning",
        duration: 2500,
        isClosable: true,
        position: "top-right",
      })
      return
    }

    if (isUpdate) {
      handleUpdate(data)
    } else {
      handleSave(data)
    }
  }

  const handleDelete = async (uuid) => {
    setLoading(true)
    try {
      await dispatch(deleteLanguageAsync({ uuid }))
      await refreshLanguages()
      onDeleteModalClose()
      toast({
        title: "Language Deleted",
        description: "Your language has been deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      console.error("Failed to delete language", error)
      toast({
        title: "Error",
        description: "Failed to delete language",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const openDeleteModal = (language) => {
    setDeletingLanguage(language)
    onDeleteModalOpen()
  }

  const watchedLanguage = watch("language")
  useEffect(() => {
    if (!watchedLanguage) {
      setDuplicateError(false)
      return
    }
    const dup = checkDuplicateLanguage(watchedLanguage, selectedLanguage?.uuid)
    setDuplicateError(dup)
  }, [watchedLanguage, selectedLanguage, languages])

  const can_read = watch("can_read")
  const can_write = watch("can_write")
  const can_speak = watch("can_speak")
  const isProficiencyInvalid = !can_read && !can_write && !can_speak

  return (
    <div className=" bg-mainBg " style={{ fontFamily: "Inter, sans-serif" }}>
      <div className=" bg-white rounded-xl w-full px-3 sm:px-5 py-4 shadow-sm  border-gray-100">

        <div className="flex sm:flex-row sm:items-center justify-between gap-3 mb-3 ">
          <div className="flex items-start gap-2">
            <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <FaLanguage />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl text-left font-semibold">Languages</h1>
              <p className="text-xs text-gray-500">Add and manage your Languages</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={openAddModal} colorScheme="blue" className="whitespace-nowrap shadow">
              Add <i className="fa-solid fa-plus pl-2"></i>
            </Button>
          </div>
        </div>

        {isInitialLoading ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <tbody>
                {[...Array(3)].map((_, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="px-6 py-4 w-1/3">
                      <Box>
                        <Skeleton height="16px" mb="2" />
                        <Skeleton height="12px" width="60%" />
                      </Box>
                    </td>
                    <td className="px-6 py-4 w-1/3">
                      <Box>
                        <Skeleton height="16px" mb="2" />
                        <Skeleton height="12px" width="40%" />
                      </Box>
                    </td>
                    <td className="px-6 py-4 w-1/3">
                      <Box className="flex gap-2">
                        <Skeleton height="32px" width="72px" />
                        <Skeleton height="32px" width="72px" />
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !languages || languages.length === 0 ? (
          <EmptyState
            title="languages"
            icon={FaLanguage}
            emptyMessage="Start by adding your first language proficiency"
            addButtonText="Add Your First Language"
            onAdd={openAddModal}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y ">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-6 py-3 font-semibold text-left text-gray-700">Language</th>
                  <th className="px-6 py-3 font-semibold text-left text-gray-700">Proficiency</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {languages.map((language, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{language.language}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-6">
                        <span
                          className={`inline-flex items-center text-sm ${language.can_read ? "text-green-600" : "text-red-600"
                            }`}
                        >
                          {language.can_read ? (
                            <FaCheck className="w-4 h-4 mr-1.5" />
                          ) : (
                            <FaTimes className="w-4 h-4 mr-1.5" />
                          )}
                          Read
                        </span>
                        <span
                          className={`inline-flex items-center text-sm ${language.can_write ? "text-green-600" : "text-red-600"
                            }`}
                        >
                          {language.can_write ? (
                            <FaCheck className="w-4 h-4 mr-1.5" />
                          ) : (
                            <FaTimes className="w-4 h-4 mr-1.5" />
                          )}
                          Write
                        </span>
                        <span
                          className={`inline-flex items-center text-sm ${language.can_speak ? "text-green-600" : "text-red-600"
                            }`}
                        >
                          {language.can_speak ? (
                            <FaCheck className="w-4 h-4 mr-1.5" />
                          ) : (
                            <FaTimes className="w-4 h-4 mr-1.5" />
                          )}
                          Speak
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                      <Button
                        onClick={() => openUpdateModal(language)}
                        className="rounded-lg hover:bg-mainBg transition-colors mr-2 shadow-sm"
                        colorScheme="blue"
                      >
                        <i className="fa-sharp fa-solid fa-pen"></i>
                      </Button>
                      <Button
                        onClick={() => openDeleteModal(language)}
                        colorScheme="red"
                        className="rounded-lg hover:bg-red-50 transition-colors"
                        variant="outline"
                      >
                        <i className="cursor-pointer fa-solid fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Single Language Modal for Add/Update (consistent UI) */}
      <Modal
        isOpen={isLanguageModalOpen}
        onClose={() => {
          reset()
          setDuplicateError(false)
          onLanguageModalClose()
        }}
        isCentered
        closeOnOverlayClick={!loading}
      >
        <ModalOverlay />
        <ModalContent maxW="5xl">
          {/* noValidate prevents native browser validation tooltip */}
          <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
            <ModalHeader>{isUpdate ? "Edit Language" : "Language"}</ModalHeader>
            <ModalCloseButton isDisabled={loading} />
            <ModalBody pb={6}>
              <FormControl isInvalid={duplicateError || !!errors.language} isRequired>
                <FormLabel>Language</FormLabel>
                <select
                  {...register("language", {
                    required: "Please select a language",
                  })}
                  className="border border-gray-300 rounded-md p-2 w-full text-gray-700 focus:outline-none focus:ring focus:ring-blue-500"
                  onChange={() => duplicateError && setDuplicateError(false)}
                >
                  <option value="">Select a language</option>
                  {languageOptions.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
                {duplicateError && (
                  <div className="flex items-center mt-2 text-red-500 text-sm">
                    <FaExclamationTriangle className="h-5 w-5 mr-1" />
                    <span>You have already selected this language</span>
                  </div>
                )}
                {!!errors.language && <p className="text-red-500 text-sm mt-1">{errors.language.message}</p>}
              </FormControl>

              <FormControl mt={4}>
                <FormLabel>Proficiency</FormLabel>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <input
                      id="can_read"
                      type="checkbox"
                      {...register("can_read")}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="can_read" className="text-sm text-gray-700 font-medium">
                      Can Read
                    </label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <input
                      id="can_write"
                      type="checkbox"
                      {...register("can_write")}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="can_write" className="text-sm text-gray-700 font-medium">
                      Can Write
                    </label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <input
                      id="can_speak"
                      type="checkbox"
                      {...register("can_speak")}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="can_speak" className="text-sm text-gray-700 font-medium">
                      Can Speak
                    </label>
                  </div>
                </div>
                {isProficiencyInvalid && (
                  <p className="text-red-500 text-sm mt-2">Select at least one: Read, Write, or Speak.</p>
                )}
              </FormControl>
            </ModalBody>
            <ModalFooter className="bg-logoblue">
              <Button mr={3} type="submit" isDisabled={duplicateError || loading}>
                {loading ? "Processing..." : isUpdate ? "Update" : "Save"}
              </Button>
              <Button
                onClick={() => {
                  reset()
                  setDuplicateError(false)
                  onLanguageModalClose()
                }}
                isDisabled={loading}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Language Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} isCentered>
        <ModalOverlay />
        <ModalContent maxW="md">
          <ModalHeader>Delete Language</ModalHeader>
          <ModalCloseButton isDisabled={loading} />
          <ModalBody pb={6}>
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
                <FaExclamationTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Language</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete "{deletingLanguage?.language}"?
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="red"
              mr={3}
              onClick={() => deletingLanguage && handleDelete(deletingLanguage?.uuid)}
              isDisabled={loading || !deletingLanguage}
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
            <Button onClick={onDeleteModalClose} isDisabled={loading}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default Language